import { useState, useEffect } from 'react';
import { notificationRulesApi, schoolsApi, roomsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Bell, Trash2, Edit, Mail, MessageSquare, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function NotificationRulesPage() {
  const [rules, setRules] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [schoolFilter, setSchoolFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    school_id: '',
    channel: 'email',
    target_email: '',
    target_webhook_url: '',
    trigger_on_vape: true,
    trigger_on_suspect: false,
    trigger_on_offline: true,
    departments: [],
  });
  const [schoolDepts, setSchoolDepts] = useState([]);

  useEffect(() => {
    loadData();
  }, [schoolFilter]);

  useEffect(() => {
    if (!formData.school_id) { setSchoolDepts([]); return; }
    roomsApi.list(formData.school_id)
      .then(res => setSchoolDepts([...new Set(res.data.map(r => (r.department || '').trim()).filter(Boolean))].sort()))
      .catch(() => setSchoolDepts([]));
  }, [formData.school_id]);

  const deptOptions = [...new Set([...schoolDepts, ...formData.departments])];
  const toggleDept = (d) => setFormData(f => ({
    ...f, departments: f.departments.includes(d) ? f.departments.filter(x => x !== d) : [...f.departments, d],
  }));

  const loadData = async () => {
    try {
      const [rulesRes, schoolsRes] = await Promise.all([
        notificationRulesApi.list(schoolFilter !== 'all' ? schoolFilter : undefined),
        schoolsApi.list(),
      ]);
      setRules(rulesRes.data);
      setSchools(schoolsRes.data);
    } catch (error) {
      toast.error('Kunde inte ladda larmregler');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (formData.channel === 'email') {
        submitData.target_webhook_url = null;
      } else {
        submitData.target_email = null;
      }
      
      if (editingRule) {
        await notificationRulesApi.update(editingRule.id, submitData);
        toast.success('Regeln uppdaterades');
      } else {
        await notificationRulesApi.create(submitData);
        toast.success('Regeln skapades');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Vill du ta bort regeln?')) return;
    try {
      await notificationRulesApi.delete(id);
      toast.success('Regeln togs bort');
      loadData();
    } catch (error) {
      toast.error('Kunde inte ta bort regeln');
    }
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      school_id: rule.school_id,
      channel: rule.channel,
      target_email: rule.target_email || '',
      target_webhook_url: rule.target_webhook_url || '',
      trigger_on_vape: rule.trigger_on_vape,
      trigger_on_suspect: rule.trigger_on_suspect,
      trigger_on_offline: rule.trigger_on_offline,
      departments: rule.departments || [],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      school_id: '',
      channel: 'email',
      target_email: '',
      target_webhook_url: '',
      trigger_on_vape: true,
      trigger_on_suspect: false,
      trigger_on_offline: true,
      departments: [],
    });
  };

  const getSchoolName = (schoolId) => {
    return schools.find(s => s.id === schoolId)?.name || 'Okänd';
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar larmregler...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="notification-rules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
            Larmregler
          </h1>
          <p className="text-slate-500 mt-1 font-ibm">
            Konfigurera vilka som notifieras vid larm
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-navy hover:bg-navy/90 rounded-sm font-ibm" data-testid="add-rule-btn">
              <Plus className="h-4 w-4 mr-2" />
              Lägg till regel
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-chivo">
                {editingRule ? 'Redigera larmregel' : 'Skapa larmregel'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Regelnamn *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="T.ex. Rektorsaviseringar"
                  required
                  className="bg-slate-50"
                  data-testid="rule-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Skola *</Label>
                <Select 
                  value={formData.school_id} 
                  onValueChange={(value) => setFormData({ ...formData, school_id: value })}
                  disabled={!!editingRule}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="rule-school-select">
                    <SelectValue placeholder="Välj en skola" />
                  </SelectTrigger>
                  <SelectContent>
                    {schools.map((school) => (
                      <SelectItem key={school.id} value={school.id}>
                        {school.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Kanal *</Label>
                <Select 
                  value={formData.channel} 
                  onValueChange={(value) => setFormData({ ...formData, channel: value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="rule-channel-select">
                    <SelectValue placeholder="Välj kanal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">E-post</SelectItem>
                    <SelectItem value="teams">Microsoft Teams</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.channel === 'email' && (
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest">E-postadress *</Label>
                  <Input
                    type="email"
                    value={formData.target_email}
                    onChange={(e) => setFormData({ ...formData, target_email: e.target.value })}
                    placeholder="namn@skola.se"
                    required
                    className="bg-slate-50"
                    data-testid="rule-email-input"
                  />
                </div>
              )}

              {formData.channel === 'teams' && (
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest">Teams webhook-URL *</Label>
                  <Input
                    value={formData.target_webhook_url}
                    onChange={(e) => setFormData({ ...formData, target_webhook_url: e.target.value })}
                    placeholder="https://outlook.office.com/webhook/..."
                    required
                    className="bg-slate-50"
                    data-testid="rule-webhook-input"
                  />
                </div>
              )}

              {deptOptions.length > 0 && (
                <div className="space-y-2" data-testid="rule-departments">
                  <Label className="font-ibm text-xs uppercase tracking-widest">Avdelningar</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {deptOptions.map(d => (
                      <button type="button" key={d} onClick={() => toggleDept(d)}
                        className={cn("px-2.5 py-1 rounded text-xs font-ibm border transition-colors",
                          formData.departments.includes(d) ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-300 hover:border-navy")}
                        data-testid={`rule-dept-${d}`}>{d}</button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 font-ibm">
                    {formData.departments.length ? 'Larm skickas bara från valda avdelningar.' : 'Ingen vald = larm från hela skolan.'}
                  </p>
                </div>
              )}

              <div className="border-t pt-4 space-y-3">
                <h4 className="font-ibm font-semibold text-sm">Utlös vid händelse</h4>
                
                <div className="flex items-center justify-between">
                  <Label className="font-ibm">Misstänkt vejping</Label>
                  <Switch
                    checked={formData.trigger_on_vape}
                    onCheckedChange={(checked) => setFormData({ ...formData, trigger_on_vape: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label className="font-ibm">Indikation på vejping</Label>
                  <Switch
                    checked={formData.trigger_on_suspect}
                    onCheckedChange={(checked) => setFormData({ ...formData, trigger_on_suspect: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label className="font-ibm">Sensor offline</Label>
                  <Switch
                    checked={formData.trigger_on_offline}
                    onCheckedChange={(checked) => setFormData({ ...formData, trigger_on_offline: checked })}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full bg-navy rounded-sm" data-testid="rule-submit-btn">
                {editingRule ? 'Uppdatera regel' : 'Skapa regel'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter */}
      <div>
        <Select value={schoolFilter} onValueChange={setSchoolFilter}>
          <SelectTrigger className="w-[200px] bg-white" data-testid="school-filter">
            <SelectValue placeholder="Filtrera på skola" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla skolor</SelectItem>
            {schools.map((school) => (
              <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Rules List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rules.map((rule) => (
          <Card key={rule.id} className="border-slate-200" data-testid={`rule-card-${rule.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "h-10 w-10 rounded-sm flex items-center justify-center",
                    rule.channel === 'email' ? "bg-blue-100" : "bg-purple-100"
                  )}>
                    {rule.channel === 'email' 
                      ? <Mail className="h-5 w-5 text-blue-600" />
                      : <MessageSquare className="h-5 w-5 text-purple-600" />
                    }
                  </div>
                  <div>
                    <h3 className="font-ibm font-semibold text-slate-900">{rule.name}</h3>
                    <p className="text-xs text-slate-500">{getSchoolName(rule.school_id)}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-500">Kanal</span>
                  <span className="font-ibm capitalize">{rule.channel === 'email' ? 'E-post' : 'Teams'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Mottagare</span>
                  <span className="font-ibm text-slate-700 truncate max-w-[150px]">
                    {rule.channel === 'email' ? rule.target_email : 'Webhook'}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">Avdelningar</span>
                  <span className="font-ibm text-slate-700 text-right flex items-center gap-1" data-testid={`rule-depts-${rule.id}`}>
                    <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    {rule.departments?.length ? rule.departments.join(', ') : 'Hela skolan'}
                  </span>
                </div>
                <div className="flex gap-2 mt-2">
                  {rule.trigger_on_vape && (
                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Misstänkt vejping</span>
                  )}
                  {rule.trigger_on_suspect && (
                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Indikation</span>
                  )}
                  {rule.trigger_on_offline && (
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">Offline</span>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => openEditDialog(rule)}
                  data-testid={`edit-rule-${rule.id}`}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Redigera
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete(rule.id)}
                  data-testid={`delete-rule-${rule.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {rules.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-chivo text-lg text-slate-700">Inga larmregler ännu</h3>
            <p className="text-slate-500 mt-1 font-ibm">
              Skapa regler för att få aviseringar vid larm, eller använd ”Skapa larmregler” under Kundregistrering.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
